import {announce} from '@github-ui/aria-live'
import {LABELS} from '@github-ui/issues-shared/labels'
import {useMutation, useQueryClient} from '@github-ui/react-query'

import type {CreateSavedViewResponse, SetSavedViewPinnedVariables} from '../components/views/types'
import {
  invalidateSavedViewsList,
  isPinLimitError,
  pinnedMutationErrorMessage,
  primeSavedViewCaches,
  type SavedViewMutationOptions,
  sendSavedViewMutationJson,
} from '../utils/saved-view-mutations'
import {type SavedViewPinSource, useSavedViewAnalytics} from './use-saved-view-analytics'

const pinnedErrorLabel = (pinned: boolean) => (pinned ? LABELS.views.pinError : LABELS.views.unpinError)

export function useSetSavedViewPinnedMutation({
  owner,
  repo,
  source,
}: SavedViewMutationOptions & {source: SavedViewPinSource}) {
  const queryClient = useQueryClient()
  const {trackViewPinned, trackViewUnpinned} = useSavedViewAnalytics()

  return useMutation<CreateSavedViewResponse, Error, SetSavedViewPinnedVariables>({
    mutationFn: ({id, pinned}) =>
      sendSavedViewMutationJson<CreateSavedViewResponse>(
        `/${owner}/${repo}/issues/views/${id}/pinned.json`,
        {method: 'PUT', body: {pinned}},
        pinnedErrorLabel(pinned),
      ),
    onSuccess: ({savedView}) => {
      primeSavedViewCaches({queryClient, owner, repo}, savedView)
      announce(savedView.pinned ? LABELS.views.pinSuccess(savedView.name) : LABELS.views.unpinSuccess(savedView.name))
      if (savedView.pinned) {
        trackViewPinned(savedView.id, source)
      } else {
        trackViewUnpinned(savedView.id, source)
      }
    },
    onError: (error, {pinned}) => {
      announce(pinnedMutationErrorMessage(error, pinned), {assertive: true})
      if (isPinLimitError(error, pinned)) {
        invalidateSavedViewsList({queryClient, owner, repo})
      }
    },
  })
}
